import type { PublicEvent } from '@shady-pines/db';
import { isNonNil } from '@shady-pines/types';
import { isNil } from 'lodash';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useApiStore } from '../api/api-store';
import { EventApi } from './event-api';
import { EventDetailsApi } from './event-details-api';
import { EventVenueApi } from './event-venue-api';
import type { EventState } from './types';
import { eventMatchesFilters } from './utils';

const initialState = {
  loading: {
    events: false,
    subscriptions: false,
    updateEventSubscriptionById: {},
    updateEvent: false,
    venues: false,
    featuredUsers: false,
    eventCategories: false,
    allCategories: false,
    createVenue: false,
    createEventCategory: false,
    createEvent: false,
  },

  events: null,
  eventVenues: null,
  eventSubscriptionsById: null,
  featuredUsersByEventId: null,
  categoriesByEventId: null,
  allCategories: null,
  lastDataFetchedFromApiAt: null,
  eventFilters: null,
  searchQuery: null,
  editableEvent: null,
  editableVenue: null,

  lastEventRefresh: null,
  lastEventVenueRefresh: null,
} as const satisfies EventState;

type UtilityFunctions = {
  getFilteredEvents: () => PublicEvent[];
};

export const useEventStore = create<
  EventState & EventApi & UtilityFunctions & EventDetailsApi & EventVenueApi
>()(
  persist(
    (set, get) => ({
      ...initialState,
      ...EventApi(get, set),
      ...EventDetailsApi(get, set),
      ...EventVenueApi(get, set),
      getFilteredEvents: () => {
        const events = get().events;
        const query = get().searchQuery;

        const categories = get().allCategories;
        if (isNil(events)) {
          return [];
        }

        return events.filter((event) => {
          const categoryIdsForEvent = get().categoriesByEventId?.[event.id] ?? [];

          const categoriesForEvent = categoryIdsForEvent
            .map((c) => categories?.[c])
            .filter(isNonNil);

          return eventMatchesFilters({
            event,
            query,
            filter: get().eventFilters,
            categoriesForEvent,
          });
        });
      },
    }),
    {
      name: 'event-storage',
      skipHydration: true,
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          const store = useApiStore.getState().localStores.event;
          return store?.getItem(name) ?? null;
        },
        setItem: (name: string, value: string) => {
          const store = useApiStore.getState().localStores.event;
          store?.setItem(name, value);
        },
        removeItem: (name: string) => {
          const store = useApiStore.getState().localStores.event;
          store?.removeItem(name);
        },
      })),
    },
  ),
);
